import React from 'react';
import { Landmark, CheckCircle, XCircle, Eye, Clock, AlertTriangle, Building2, FileText, Copy } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

// Badge de statut
export const StatusBadge = ({ status }) => {
  const configs = {
    pending_virement: { label: 'En attente', color: 'bg-blue-500 text-white', icon: Clock },
    paid: { label: 'Validé', color: 'bg-emerald-500 text-white', icon: CheckCircle },
    rejected: { label: 'Rejeté', color: 'bg-red-500 text-white', icon: XCircle },
    expired: { label: 'Expiré', color: 'bg-gray-500 text-white', icon: AlertTriangle }
  };

  const config = configs[status] || configs.pending_virement;
  const Icon = config.icon;

  return (
    <Badge className={`${config.color} flex items-center gap-1 font-medium`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

// Carte d'adhésion en attente de virement
export const VirementCard = ({ adhesion, onValidate, onReject, onView, isSelected, onToggleSelect }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const copyReference = () => {
    navigator.clipboard.writeText(adhesion.virement_reference || '');
    toast.success('Référence copiée !');
  };

  const now = Date.now();
  const daysSinceRequest = adhesion.virement_requested_at
    ? Math.floor((now - new Date(adhesion.virement_requested_at)) / (1000 * 60 * 60 * 24))
    : 0;

  // Déterminer si les actions sont disponibles selon le statut
  const isPending = adhesion.payment_status === 'pending_virement';
  const isValidated = adhesion.payment_status === 'paid';
  const isRejected = adhesion.payment_status === 'rejected';

  // Couleur de bordure selon le statut
  const getBorderColor = () => {
    if (isValidated) return 'border-l-emerald-500';
    if (isRejected) return 'border-l-red-500';
    return daysSinceRequest > 5 ? 'border-l-red-500' : 'border-l-blue-500';
  };

  return (
    <Card className={`border-l-4 ${getBorderColor()} hover:shadow-lg transition-shadow ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}>
      <CardContent className="p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Checkbox de sélection - uniquement pour les en attente */}
          {isPending && (
            <div className="flex items-start">
              <Checkbox
                checked={isSelected}
                onCheckedChange={onToggleSelect}
                className="h-6 w-6 border-2 border-slate-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              />
            </div>
          )}

          {/* Info principale */}
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isValidated ? 'bg-emerald-100' : isRejected ? 'bg-red-100' : 'bg-blue-100'
              }`}>
                <Building2 className={`w-5 h-5 ${
                  isValidated ? 'text-emerald-600' : isRejected ? 'text-red-600' : 'text-blue-600'
                }`} />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">{adhesion.raison_sociale || 'Entreprise'}</h4>
                <p className="text-sm text-gray-500">{adhesion.email || adhesion.membre_email}</p>
              </div>
              <StatusBadge status={adhesion.payment_status} />
            </div>

            {/* Référence virement */}
            <div className={`flex items-center gap-2 p-2 rounded-lg border ${
              isValidated ? 'bg-emerald-50 border-emerald-200' :
              isRejected ? 'bg-red-50 border-red-200' :
              'bg-blue-50 border-blue-200'
            }`}>
              <Landmark className={`w-4 h-4 ${
                isValidated ? 'text-emerald-600' : isRejected ? 'text-red-600' : 'text-blue-600'
              }`} />
              <span className={`text-sm font-medium ${
                isValidated ? 'text-emerald-800' : isRejected ? 'text-red-800' : 'text-blue-800'
              }`}>Référence:</span>
              <code className={`font-mono font-bold ${
                isValidated ? 'text-emerald-700' : isRejected ? 'text-red-700' : 'text-blue-700'
              }`}>{adhesion.virement_reference}</code>
              <button onClick={copyReference} className="ml-auto p-1 hover:bg-white/50 rounded">
                <Copy className="w-3 h-3 text-gray-600" />
              </button>
            </div>

            {/* Détails */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Type:</span>
                <p className="font-medium">{adhesion.type_adhesion || 'TPE'}</p>
              </div>
              <div>
                <span className="text-gray-500">Territoire:</span>
                <p className="font-medium">{adhesion.territoire || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Montant:</span>
                <p className={`font-bold ${isValidated ? 'text-emerald-600' : 'text-blue-600'}`}>200 €</p>
              </div>
              <div>
                <span className="text-gray-500">{isValidated ? 'Validé le:' : isRejected ? 'Rejeté le:' : 'Demandé le:'}</span>
                <p className="font-medium">
                  {formatDate(isValidated ? adhesion.validated_at : isRejected ? adhesion.rejected_at : adhesion.virement_requested_at)}
                </p>
              </div>
            </div>

            {/* Alerte si ancien et en attente */}
            {isPending && daysSinceRequest > 5 && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertTriangle className="w-4 h-4" />
                Demande en attente depuis {daysSinceRequest} jours
              </div>
            )}

            {/* Message si validé */}
            {isValidated && (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                  <CheckCircle className="w-4 h-4" />
                  Virement validé - Adhésion activée
                </div>
                <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700" data-testid="registre-feed-badge">
                  <FileText className="w-3.5 h-3.5" />
                  Inscrit automatiquement au Registre FTPE
                </div>
              </div>
            )}

            {/* Message si rejeté */}
            {isRejected && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <XCircle className="w-4 h-4" />
                Virement rejeté {adhesion.reject_reason ? `- ${adhesion.reject_reason}` : ''}
              </div>
            )}
          </div>

          {/* Actions - conditionnées selon le statut */}
          <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:w-40">
            {/* Boutons Valider/Rejeter seulement si en attente */}
            {isPending && (
              <>
                <Button
                  onClick={() => onValidate(adhesion)}
                  className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Valider
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onReject(adhesion)}
                  className="border-red-300 text-red-600 hover:bg-red-50 flex-1"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
              </>
            )}

            {/* Indicateur pour les validés */}
            {isValidated && (
              <div className="flex items-center justify-center gap-2 py-2 px-3 bg-emerald-100 rounded-lg text-emerald-700 font-medium">
                <CheckCircle className="w-4 h-4" />
                Traité
              </div>
            )}

            {/* Indicateur pour les rejetés */}
            {isRejected && (
              <div className="flex items-center justify-center gap-2 py-2 px-3 bg-red-100 rounded-lg text-red-700 font-medium">
                <XCircle className="w-4 h-4" />
                Rejeté
              </div>
            )}

            {/* Bouton détails toujours visible */}
            <Button
              variant="ghost"
              onClick={() => onView(adhesion)}
              className="flex-1"
            >
              <Eye className="w-4 h-4 mr-2" />
              Détails
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
